import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createSeatingChartApi,
  getSiteSeatingChartsApi,
  getEventSeatingChartApi,
  updateSeatingChartApi,
  deleteSeatingChartApi,
  updateTicketAssignmentsApi,
  getChartCategoriesApi,
  getDesignerTokenApi,
  getTemplatesApi,
  getTemplateChartsApi,
  saveAsTemplateApi,
} from './api';
import {
  CreateSeatingChartPayload,
  UpdateSeatingChartPayload,
  UpdateTicketAssignmentsPayload,
} from '../types';

export const createSeatingChart = createAsyncThunk(
  'seatingCharts/create',
  async (payload: CreateSeatingChartPayload) => {
    const response = await createSeatingChartApi(payload);
    return response.data.chart;
  },
);

export const fetchSiteSeatingCharts = createAsyncThunk(
  'seatingCharts/fetchSiteCharts',
  async () => {
    const response = await getSiteSeatingChartsApi();
    return response.data.charts;
  },
);

export const fetchEventSeatingChart = createAsyncThunk(
  'seatingCharts/fetchForEvent',
  async (eventId: string) => {
    const response = await getEventSeatingChartApi(eventId);
    return response.data.chart;
  },
);

export const updateSeatingChart = createAsyncThunk(
  'seatingCharts/update',
  async (payload: UpdateSeatingChartPayload) => {
    const response = await updateSeatingChartApi(payload);
    return response.data.chart;
  },
);

export const deleteSeatingChart = createAsyncThunk(
  'seatingCharts/delete',
  async (chartId: string) => {
    await deleteSeatingChartApi(chartId);
    return chartId;
  },
);

export const updateTicketAssignments = createAsyncThunk(
  'seatingCharts/updateTicketAssignments',
  async (payload: UpdateTicketAssignmentsPayload) => {
    // Deduplicate ticket assignments before sending to API
    // Keep only the latest assignment per ticket (by ticketUuid or ticketId)
    // If the latest assignment has no category, remove all assignments for that ticket
    const deduplicatedAssignments: any[] = [];
    const seenTickets = new Map<string, any>();

    // Group by ticketUuid (preferred) or ticketId
    payload.ticket_assignments.forEach((assignment) => {
      const key = (assignment as any).ticketUuid || assignment.ticketId;
      // Always keep the latest assignment (last one in array wins)
      seenTickets.set(key, assignment);
    });

    // Only include assignments that have a category (user checked it)
    // Exclude assignments without category (user unchecked it)
    seenTickets.forEach((assignment) => {
      if (assignment.category && assignment.category.trim() !== '') {
        deduplicatedAssignments.push(assignment);
      }
    });

    const response = await updateTicketAssignmentsApi({
      ...payload,
      ticket_assignments: deduplicatedAssignments,
    });
    return {
      chart: response.data.chart,
      allTicketsAssigned: response.data.all_tickets_assigned,
    };
  },
);

export const fetchChartCategories = createAsyncThunk(
  'seatingCharts/fetchCategories',
  async (chartId: string) => {
    const response = await getChartCategoriesApi(chartId);
    return response.data.categories;
  },
);

export const fetchDesignerToken = createAsyncThunk(
  'seatingCharts/fetchDesignerToken',
  async (chartId: string) => {
    const response = await getDesignerTokenApi(chartId);
    return response.data;
  },
);

export const fetchTemplates = createAsyncThunk(
  'seatingCharts/fetchTemplates',
  async (siteId: string) => {
    const response = await getTemplatesApi(siteId);
    return response.data.templates;
  },
);

export const saveAsTemplate = createAsyncThunk(
  'seatingCharts/saveAsTemplate',
  async ({ chartId, templateName }: { chartId: string; templateName: string }) => {
    const response = await saveAsTemplateApi(chartId, templateName);
    return response.data.chart;
  },
);

export const fetchTemplateCharts = createAsyncThunk(
  'seatingCharts/fetchTemplateCharts',
  async () => {
    const response = await getTemplateChartsApi();
    return response.data.templates;
  },
);
